// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import styled, { css } from 'styled-components';

import { visuallyHidden } from '../../../utils/style';
import { baseSpacing, baseStyles } from '../Base';
import { ButtonProps } from './';

export const StyledButton = styled.button<ButtonProps>`
  border-radius: ${(props) => props.theme.radii.round};
  font-size: ${(props) => props.theme.buttonSizes[props.size || 'lg'].fontSize};
  font-weight: ${(props) =>
    props.theme.buttonSizes[props.size || 'lg'].fontWeight};
  padding: ${(props) => props.theme.buttonSizes[props.size || 'lg'].padding};
  border-color: transparent;
  transition: background-color 0.1s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: ${(props) =>
    props.theme.buttonSizes[props.size || 'lg'].lineHeight};
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }

  &:focus {
    outline: none;
  }

  .ch-icon {
    display: flex;
    width: ${(props) => props.theme.iconButtonSizes[props.iconSize || 'sm']};
    height: ${(props) => props.theme.iconButtonSizes[props.iconSize || 'sm']};
  }

  .ch-icon + .ch-label,
  .ch-label + .ch-icon {
    margin-left: ${(props) =>
      props.theme.buttonSizes[props.size || 'lg'].iconSpace};
  }

  ${(props) =>
    props.onlyIconAtSp &&
    css`
      width: 2rem;
      height: 2rem;
      border-radius: ${(props) => props.theme.radii.circle};

      .ch-label {
        display: none;
      }

      .ch-icon + .ch-label,
      .ch-label + .ch-icon {
        margin-left: 0;
      }
    `}

  /* variant styles */
  ${(props) => props.variant === 'primary' && StyledPrimaryButton}
  ${(props) => props.variant === 'secondary' && StyledSecondaryButton}
  ${(props) => props.variant === 'info' && StyledInfoButton}
  ${(props) => props.variant === 'error' && StyledErrorButton}
  ${(props) => props.variant === 'icon' && StyledIconButton}

  ${(props) => props.theme.mediaQueries.min.md} {
    &:not(:disabled):hover {
      opacity: 0.7;
    }

    ${(props) =>
      props.onlyIconAtSp &&
      css`
        width: auto;
        height: auto;
        border-radius: ${(props) => props.theme.radii.round};

        .ch-label {
          display: initial;
        }

        .ch-icon + .ch-label,
        .ch-label + .ch-icon {
          margin-left: ${props.theme.buttonSizes[props.size || 'lg'].iconSpace};
        }
      `}
  }

  ${baseSpacing}
  ${baseStyles}
`;

export const StyledPrimaryButton = css<ButtonProps>`
  background-color: ${(props) => props.theme.buttons.primary.static.bgd};
  color: ${(props) => props.theme.buttons.primary.static.text};
  border: ${(props) => props.theme.buttons.primary.static.border};
  box-shadow: ${(props) => props.theme.buttons.primary.static.shadow};

  &:disabled {
    background-color: ${(props) => props.theme.buttons.primary.disabled.bgd};
    border: ${(props) => props.theme.buttons.primary.disabled.border};
    color: ${(props) => props.theme.buttons.primary.disabled.text};
    opacity: 1;
    cursor: not-allowed;
  }
`;

export const StyledSecondaryButton = css<ButtonProps>`
  background-color: ${(props) => props.theme.buttons.secondary.static.bgd};
  color: ${(props) => props.theme.buttons.secondary.static.text};
  border: ${(props) => props.theme.buttons.secondary.static.border};
  box-shadow: ${(props) => props.theme.buttons.secondary.static.shadow};
  font-weight: normal;

  &:disabled {
    background-color: ${(props) => props.theme.buttons.secondary.disabled.bgd};
    border: ${(props) => props.theme.buttons.secondary.disabled.border};
    color: ${(props) => props.theme.buttons.secondary.disabled.text};
    opacity: 1;
    cursor: not-allowed;
  }
`;

export const StyledInfoButton = css<ButtonProps>`
  background-color: ${(props) =>
    props.outline
      ? props.theme.buttons.info.outline.bgd
      : props.theme.buttons.info.static.bgd};
  color: ${(props) =>
    props.outline
      ? props.theme.buttons.info.outline.text
      : props.theme.buttons.info.static.text};
  border: ${(props) =>
    props.outline
      ? `1px solid ${props.theme.buttons.info.outline.border}`
      : props.theme.buttons.info.static.border};
  box-shadow: ${(props) =>
    props.outline
      ? props.theme.buttons.info.outline.shadow
      : props.theme.buttons.info.static.shadow};

  &:disabled {
    background-color: ${(props) => props.theme.buttons.info.disabled.bgd};
    border: ${(props) => props.theme.buttons.info.disabled.border};
    color: ${(props) => props.theme.buttons.info.disabled.text};
    opacity: 1;
    cursor: not-allowed;
  }
`;

export const StyledErrorButton = css<ButtonProps>`
  background-color: ${(props) =>
    props.outline
      ? props.theme.buttons.error.outline.bgd
      : props.theme.buttons.error.static.bgd};
  color: ${(props) =>
    props.outline
      ? props.theme.buttons.error.outline.text
      : props.theme.buttons.error.static.text};
  border: ${(props) =>
    props.outline
      ? `1px solid ${props.theme.buttons.error.outline.border}`
      : props.theme.buttons.error.static.border};
  box-shadow: ${(props) =>
    props.outline
      ? props.theme.buttons.error.outline.shadow
      : props.theme.buttons.error.static.shadow};

  &:disabled {
    background-color: ${(props) => props.theme.buttons.primary.disabled.bgd};
    border: ${(props) => props.theme.buttons.primary.disabled.border};
    color: ${(props) => props.theme.buttons.primary.disabled.text};
    opacity: 1;
    cursor: not-allowed;
  }
`;

const badgeLayout = {
  sm: css`
    top: -15%;
    left: 76%;
  `,
  md: css`
    top: 4%;
    left: 76%;
  `,
  lg: css`
    top: 10%;
    left: 76%;
  `,
  auto: css`
    top: 10%;
    left: 76%;
  `
};

export const StyledIconButton = css<ButtonProps>`
  background-color: ${(props) =>
    props.selected
      ? props.theme.buttons.icon.selected.bgd
      : props.theme.buttons.icon.static.bgd};
  color: ${(props) =>
    props.selected
      ? props.theme.buttons.icon.selected.text
      : props.theme.buttons.icon.static.text};
  border: ${(props) =>
    props.selected
      ? props.theme.buttons.icon.selected.border
      : props.theme.buttons.icon.static.border};
  border-radius: ${(props) => props.theme.radii.circle};
  padding: 0.1875rem;
  position: relative;

  > .ch-label {
    ${visuallyHidden};
  }

  > .ch-icon {
    width: ${(props) => props.theme.iconButtonSizes[props.iconSize || 'sm']};
    height: ${(props) => props.theme.iconButtonSizes[props.iconSize || 'sm']};
    margin: 0;
  }

  &:focus {
    background-color: ${({ theme, selected }) =>
      selected
        ? theme.buttons.icon.selected.bgd
        : theme.buttons.icon.static.bgd};
    border: ${(props) => props.theme.buttons.icon.focus.border};
    color: ${(props) => props.theme.buttons.icon.focus.text};
    color: ${({ theme, selected }) =>
      selected
        ? theme.buttons.icon.selected.text
        : theme.buttons.icon.static.text};
    box-shadow: ${(props) => props.theme.buttons.icon.focus.shadow};
  }

  &:hover {
    background-color: ${(props) => props.theme.buttons.icon.hover.bgd};
    border: ${(props) => props.theme.buttons.icon.hover.border};
    color: ${(props) => props.theme.buttons.icon.hover.text};
  }

  &:active {
    background-color: ${(props) => props.theme.buttons.icon.active.bgd};
    border: ${(props) => props.theme.buttons.icon.active.border};
    color: ${(props) => props.theme.buttons.icon.active.text};
  }

  &:disabled {
    background-color: ${(props) => props.theme.buttons.icon.disabled.bgd};
    border: ${(props) => props.theme.buttons.icon.disabled.border};
    color: ${(props) => props.theme.buttons.icon.disabled.text};
    cursor: not-allowed;
  }

  + * {
    position: absolute;
    font-size: 0.55rem;
    z-index: 1;
    ${({ iconSize }) => badgeLayout[iconSize || 'sm']}
  }
`;

export const StyledIconButtonWrapper = styled.span`
  display: inline-block;
  position: relative;
`;
